import { css } from 'styled-components';
import React from 'react';
import { BaseStateKey, MapColourStates, StateColour } from './colourInterfaces';
import { Icon } from '../../components/Icon/Icon';
import { useFilterColourCss } from './filter';
import { useActionCss } from '../actions';

export const MAIN_BRAND_COLOURS = {
  ACCENT: 'accent',
  PRIMARY: 'primary'
} as const;
export type MainBrandColour = (typeof MAIN_BRAND_COLOURS)[keyof typeof MAIN_BRAND_COLOURS];

export const BRAND_COLOURS = {
  ...MAIN_BRAND_COLOURS,
  SECONDARY: 'secondary'
} as const;
export type BrandColour = (typeof BRAND_COLOURS)[keyof typeof BRAND_COLOURS];

export const EXTRA_BRAND_COLOURS = {
  ...BRAND_COLOURS,
  SECONDARY_INVERTED: 'secondaryInverted',
  CLOSEBUTTONONPRIMARY: 'closeButtonOnPrimary'
} as const;
export type ExtraBrandColour = (typeof EXTRA_BRAND_COLOURS)[keyof typeof EXTRA_BRAND_COLOURS];

export const BASIC_COLOURS = {
  WHITE: 'white',
  BLACK: 'black'
} as const;
export type BasicColour = (typeof BASIC_COLOURS)[keyof typeof BASIC_COLOURS];

export const BASIC_NO_SWITCH_COLOURS = {
  WHITE_NO_SWITCH: 'whiteNoSwitch',
  BLACK_NO_SWITCH: 'blackNoSwitch'
} as const;
export type BasicNoSwitchColours = (typeof BASIC_NO_SWITCH_COLOURS)[keyof typeof BASIC_NO_SWITCH_COLOURS];

export const OPAQUE_COLOURS = {
  OPAQUE_WHITE: 'opaqueWhite',
  OPAQUE_BLACK: 'opaqueBlack'
} as const;
export type OpaqueColour = (typeof OPAQUE_COLOURS)[keyof typeof OPAQUE_COLOURS];

export const BASE_STATUS_COLOURS = {
  INFO: 'info',
  ERROR: 'error',
  WARNING: 'warning',
  SUCCESS: 'success'
} as const;
export type BaseStatusColour = (typeof BASE_STATUS_COLOURS)[keyof typeof BASE_STATUS_COLOURS];

export const STATUS_COLOURS = {
  ...BASE_STATUS_COLOURS,
  NEUTRAL: 'neutral'
} as const;
export type StatusColour = (typeof STATUS_COLOURS)[keyof typeof STATUS_COLOURS];

// TODO: Revisit once alert is refactored. Might delete.
export const SEVERITY_COLOURS = {
  ...BASE_STATUS_COLOURS,
  SECONDARY: BRAND_COLOURS.SECONDARY
} as const;
export type SeverityColour = (typeof SEVERITY_COLOURS)[keyof typeof SEVERITY_COLOURS];

export const TRADING_VARIANTS = {
  POSITIVE: 'positive',
  NEGATIVE: 'negative',
  UNCHANGED: 'unchanged'
} as const;
export type TradingVariant = (typeof TRADING_VARIANTS)[keyof typeof TRADING_VARIANTS];

export const DEFAULT_DISABLED_STATE = {
  opacity: 0.5
};

export const generateColourState = (props: StateColour, useLight?: boolean, skipDefaultDisabledState?: boolean) => {
  const { text, colour, hover, active } = useLight && props.light ? props.light : props;
  const colourStates: MapColourStates = {
    text,
    background: colour,
    hover: {
      background: hover
    },
    active: {
      background: active
    }
  };

  if (!skipDefaultDisabledState) {
    colourStates.disabled = DEFAULT_DISABLED_STATE;
  }

  return colourStates;
};

export type StateSelectorVariant = ExtraBrandColour | StatusColour | BasicColour | OpaqueColour;
export interface IStateSelectorProps {
  variant: StateSelectorVariant;
  light?: boolean;
  cssState?: BaseStateKey;
  onClick?: React.MouseEventHandler;
  disabled?: boolean;
}

export const useColourWithStatesCss = css<IStateSelectorProps>`
  ${({ theme, variant, light, cssState, onClick, disabled }) => {
    let selectedColour: StateColour;

    switch (variant) {
      case 'accent':
      case 'primary':
      case 'secondary':
      case 'secondaryInverted':
      case 'closeButtonOnPrimary': {
        selectedColour = theme.colours[variant];
        break;
      }
      /* istanbul ignore next */ case 'white':
      /* istanbul ignore next */ case 'black': {
        selectedColour = theme.colours.base[variant];
        break;
      }
      case 'opaqueWhite':
      case 'opaqueBlack': {
        selectedColour = theme.colours.opaque[variant === 'opaqueWhite' ? 'white' : 'black'];
        break;
      }
      default: {
        selectedColour = theme.colours.status[variant];
      }
    }

    selectedColour = light && selectedColour.light ? selectedColour.light : selectedColour;

    let backgroundColour = selectedColour.colour;

    if (cssState) {
      backgroundColour = selectedColour[cssState];
    }

    return css`
      color: ${selectedColour.text};
      background: ${backgroundColour};

      ${Icon} {
        ${useFilterColourCss(selectedColour.text)};
      }

      ${disabled &&
      css`
        ${DEFAULT_DISABLED_STATE};
      `}

      ${useActionCss};

      ${onClick &&
      !disabled &&
      css`
        &:hover {
          background: ${selectedColour.hover};
        }

        &:active {
          background: ${selectedColour.active};
        }
      `};
    `;
  }}
`;

// Note: used in Badge, FeaturedIcons
export const VARIANTS = {
  ...BASE_STATUS_COLOURS,
  ...EXTRA_BRAND_COLOURS,
  ...OPAQUE_COLOURS
} as const;
export type Variant = (typeof VARIANTS)[keyof typeof VARIANTS];

export const DEFAULT_VARIANT = VARIANTS.SECONDARY;

// Use this with caution!!
// Avatar is the only component that use this for now.
export const EXTRA_PALETTE_COLOURS = {
  MINT: 'mint',
  ROSE: 'rose',
  SKY: 'sky'
} as const;
export type ExtraPaletteColour = (typeof EXTRA_PALETTE_COLOURS)[keyof typeof EXTRA_PALETTE_COLOURS];

export const SEVERITY_TYPES = {
  ...BASIC_COLOURS,
  COLOURED: 'coloured',
  DARK: 'dark'
} as const;
export type SeverityType = (typeof SEVERITY_TYPES)[keyof typeof SEVERITY_TYPES];
